<template>
  <div class="accessory-management">
    <div class="page-header">
      <h2>配件管理</h2>
      <el-button 
        v-if="isAdmin"
        type="primary" 
        @click="showAddDialog = true"
        icon="el-icon-plus">
        添加配件
      </el-button>
    </div>

    <!-- 搜索和筛选 -->
    <div class="search-bar">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索配件名称"
            @input="handleSearch"
            clearable>
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="statusFilter" @change="handleSearch" placeholder="状态筛选">
            <el-option label="全部" value=""></el-option>
            <el-option label="启用" value="ACTIVE"></el-option>
            <el-option label="禁用" value="INACTIVE"></el-option>
          </el-select>
        </el-col>
      </el-row>
    </div>

    <!-- 配件列表 -->
    <div class="table-container">
      <el-table 
        :data="accessories" 
        v-loading="loading"
        stripe
        style="width: 100%">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="配件名称" min-width="150"></el-table-column>
        <el-table-column prop="description" label="描述" min-width="200"></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'ACTIVE' ? 'success' : 'danger'">
              {{ scope.row.status === 'ACTIVE' ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="260" v-if="isAdmin">
          <template #default="scope">
            <div class="action-cell">
              <el-button 
                size="small" 
                @click="editAccessory(scope.row)"
                icon="el-icon-edit">
                编辑
              </el-button>
              <el-button 
                v-if="scope.row.status === 'INACTIVE'"
                size="small" 
                type="success"
                @click="enableAccessory(scope.row.id)"
                icon="el-icon-check">
                启用
              </el-button>
              <el-button 
                v-if="scope.row.status === 'ACTIVE'"
                size="small" 
                type="warning"
                @click="disableAccessory(scope.row.id)"
                icon="el-icon-close">
                禁用
              </el-button>
              <el-button 
                size="small" 
                type="danger"
                @click="deleteAccessory(scope.row)"
                icon="el-icon-delete">
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange">
      </el-pagination>
    </div>

    <!-- 添加/编辑配件对话框 -->
    <el-dialog 
      v-model="showAddDialog" 
      :title="editingAccessory ? '编辑配件' : '添加配件'"
      width="500px">
      <el-form 
        :model="accessoryForm" 
        :rules="accessoryRules" 
        ref="accessoryFormRef"
        label-width="80px">
        <el-form-item label="配件名称" prop="name">
          <el-input v-model="accessoryForm.name" placeholder="请输入配件名称"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input 
            v-model="accessoryForm.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入配件描述"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="saveAccessory" :loading="saving">
            {{ editingAccessory ? '更新' : '添加' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import axios from 'axios'

export default {
  name: 'AccessoryManagement',
  components: {
    Search
  },
  setup() {
    const store = useStore()
    
    // 响应式数据
    const accessories = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const showAddDialog = ref(false)
    const editingAccessory = ref(null)
    const searchKeyword = ref('')
    const statusFilter = ref('')
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    
    // 表单数据
    const accessoryForm = reactive({
      name: '',
      description: ''
    })
    
    // 表单验证规则
    const accessoryRules = {
      name: [
        { required: true, message: '请输入配件名称', trigger: 'blur' },
        { min: 1, max: 100, message: '配件名称长度在 1 到 100 个字符', trigger: 'blur' }
      ]
    }
    
    const accessoryFormRef = ref(null)
    
    // 计算属性
    const isAdmin = computed(() => {
      const user = store.getters.currentUser
      return user && (user.role === 'ADMIN' || user.role === 'admin')
    })
    
    // 方法
    const loadAccessories = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value - 1,
          size: pageSize.value
        }
        
        if (searchKeyword.value) {
          params.keyword = searchKeyword.value
        }
        
        if (statusFilter.value) {
          params.status = statusFilter.value
        }
        
        const response = await axios.get('/api/admin/accessories', { params })
        
        if (response.data.success) {
          accessories.value = response.data.data || []
          total.value = response.data.total || 0
        } else {
          ElMessage.error(response.data.message || '加载配件列表失败')
        }
      } catch (error) {
        console.error('加载配件列表失败:', error)
        ElMessage.error('加载配件列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleSearch = () => {
      currentPage.value = 1
      loadAccessories()
    }
    
    const handleSizeChange = (newSize) => {
      pageSize.value = newSize
      currentPage.value = 1
      loadAccessories()
    }
    
    const handleCurrentChange = (newPage) => {
      currentPage.value = newPage
      loadAccessories()
    }
    
    const editAccessory = (accessory) => {
      editingAccessory.value = accessory
      accessoryForm.name = accessory.name
      accessoryForm.description = accessory.description || ''
      showAddDialog.value = true
    }
    
    const saveAccessory = async () => {
      if (!accessoryFormRef.value) return
      
      try {
        await accessoryFormRef.value.validate()
        
        saving.value = true
        
        const data = {
          name: accessoryForm.name.trim(),
          description: accessoryForm.description.trim()
        }
        
        let response
        if (editingAccessory.value) {
          // 更新配件
          response = await axios.put(`/api/admin/accessories/${editingAccessory.value.id}`, data)
        } else {
          // 创建配件
          response = await axios.post('/api/admin/accessories', data)
        }
        
        if (response.data.success) {
          ElMessage.success(editingAccessory.value ? '配件更新成功' : '配件创建成功')
          showAddDialog.value = false
          resetForm()
          loadAccessories()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('保存配件失败:', error)
        ElMessage.error('保存配件失败')
      } finally {
        saving.value = false
      }
    }
    
    const enableAccessory = async (id) => {
      try {
        const response = await axios.put(`/api/admin/accessories/${id}/enable`)
        if (response.data.success) {
          ElMessage.success('配件启用成功')
          loadAccessories()
        } else {
          ElMessage.error(response.data.message || '启用失败')
        }
      } catch (error) {
        console.error('启用配件失败:', error)
        ElMessage.error('启用配件失败')
      }
    }
    
    const disableAccessory = async (id) => {
      try {
        await ElMessageBox.confirm('确定要禁用这个配件吗？', '确认操作', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await axios.put(`/api/admin/accessories/${id}/disable`)
        if (response.data.success) {
          ElMessage.success('配件禁用成功')
          loadAccessories()
        } else {
          ElMessage.error(response.data.message || '禁用失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('禁用配件失败:', error)
          ElMessage.error('禁用配件失败')
        }
      }
    }
    
    const deleteAccessory = async (accessory) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除配件"${accessory.name}"吗？删除后无法恢复！`, 
          '危险操作', 
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'error',
            confirmButtonClass: 'el-button--danger'
          }
        )
        
        const response = await axios.delete(`/api/admin/accessories/${accessory.id}`)
        if (response.data.success) {
          ElMessage.success('配件删除成功')
          loadAccessories()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除配件失败:', error)
          ElMessage.error('删除配件失败')
        }
      }
    }
    
    const resetForm = () => {
      editingAccessory.value = null
      accessoryForm.name = ''
      accessoryForm.description = ''
      if (accessoryFormRef.value) {
        accessoryFormRef.value.resetFields()
      }
    }
    
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    }
    
    // 生命周期
    onMounted(() => {
      loadAccessories()
    })
    
    return {
      accessories,
      loading,
      saving,
      showAddDialog,
      editingAccessory,
      searchKeyword,
      statusFilter,
      currentPage,
      pageSize,
      total,
      accessoryForm,
      accessoryRules,
      accessoryFormRef,
      isAdmin,
      loadAccessories,
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      editAccessory,
      saveAccessory,
      enableAccessory,
      disableAccessory,
      deleteAccessory,
      resetForm,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.accessory-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-bar {
  margin-bottom: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.table-container {
  background: white;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.pagination {
  display: flex;
  justify-content: center;
}

.dialog-footer {
  text-align: right;
}

.el-table {
  font-size: 14px;
}

.el-table th {
  background-color: #fafafa;
  color: #606266;
  font-weight: 500;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.action-cell .el-button + .el-button {
  margin-left: 0;
}
</style>